<template>
    <section
        ref="section"
        class="slice slice-video slice-video--with-shapes"
        :class="{'slice-video--with-shapes--grey': backgroundColor === 'grey'}"
        data-testid="video-slice-with-shapes"
        aria-label="video"
    >
        <div class="slice-content slice-video--with-shapes__content">
            <div class="slice-video--with-shapes__wrapper">
                <MktVideoPlayer ref="videoPlayer" :src="videoSrc" />
                <div
                    v-if="!videoUnveiled && isFilled.image(slice.primary.coverImage)"
                    ref="coverRef"
                    class="slice-video--with-shapes__cover"
                    @click="startVideo()"
                >
                    <PrismicImage :field="slice.primary.coverImage" class="slice-video--with-shapes__cover-image" />
                    <div class="slice-video--with-shapes__cover-overlay" />
                    <div class="slice-video--with-shapes__cover-icon-wrapper">
                        <svg
                            class="slice-video--with-shapes__cover-icon"
                            width="73"
                            height="73"
                            viewBox="0 0 73 73"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            aria-hidden="true"
                        >
                            <path
                                d="M27.9791 48.0056V24.4109C27.9796 24.1157 28.0595 23.8261 28.2104 23.5723C28.3613 23.3186 28.5777 23.1101 28.8368 22.9688C29.096 22.8274 29.3884 22.7584 29.6834 22.7689C29.9785 22.7794 30.2652 22.869 30.5137 23.0284L48.868 34.8225C49.1004 34.9714 49.2917 35.1763 49.4241 35.4185C49.5566 35.6607 49.626 35.9322 49.626 36.2083C49.626 36.4843 49.5566 36.7558 49.4241 36.998C49.2917 37.2402 49.1004 37.4451 48.868 37.594L30.5137 49.3914C30.2652 49.5508 29.9785 49.6404 29.6834 49.6509C29.3884 49.6614 29.096 49.5924 28.8368 49.451C28.5777 49.3096 28.3613 49.1012 28.2104 48.8475C28.0595 48.5937 27.9796 48.3041 27.9791 48.0089V48.0056Z"
                                fill="white"
                            />
                            <path
                                d="M0 36.2083C0 16.2115 16.2115 0 36.2083 0C56.2052 0 72.4167 16.2115 72.4167 36.2083C72.4167 56.2052 56.2052 72.4167 36.2083 72.4167C16.2115 72.4167 0 56.2052 0 36.2083ZM36.2083 4.9375C27.9148 4.9375 19.9609 8.2321 14.0965 14.0965C8.2321 19.9609 4.9375 27.9148 4.9375 36.2083C4.9375 44.5019 8.2321 52.4557 14.0965 58.3201C19.9609 64.1846 27.9148 67.4792 36.2083 67.4792C44.5019 67.4792 52.4557 64.1846 58.3201 58.3201C64.1846 52.4557 67.4792 44.5019 67.4792 36.2083C67.4792 27.9148 64.1846 19.9609 58.3201 14.0965C52.4557 8.2321 44.5019 4.9375 36.2083 4.9375Z"
                                fill="white"
                            />
                        </svg>
                    </div>
                </div>
            </div>

            <div class="slice-video--with-shapes__text-wrapper">
                <MktTitleBrand :field="slice.primary.title" class="slice-video--with-shapes__title" />
                <PrismicRichText :field="slice.primary.description" class="slice-video--with-shapes__description" />
            </div>
            <div class="slice-video--with-shapes__shapes" />
        </div>
    </section>
</template>

<script setup lang="ts">
    import {computed, ref} from 'vue';
    import {isFilled} from '@prismicio/helpers';
    import {getSliceComponentProps, PrismicImage, PrismicRichText} from '@prismicio/vue';
    import type {VideoSlice, VideoSliceVideoWithShapes} from '@prismicio-types';
    import {MktTitleBrand, MktVideoPlayer} from '#components';
    import {useComponentTracking} from '#imports';
    import {useVideoEmbedPlayer} from '../../../composables/useVideoEmbedPlayer';

    const props = defineProps(getSliceComponentProps<VideoSlice>());
    const slice = computed(() => props.slice as VideoSliceVideoWithShapes);
    const section = ref(null);
    const {getEmbedSrc} = useVideoEmbedPlayer();

    const backgroundColor = computed(() => slice.value.primary.backgroundColor);
    const videoSrc = computed(() => {
        const src = getEmbedSrc(slice.value.primary.video);
        if (!src) return '';
        return src.includes('?') ? `${src}&rel=0` : `${src}?rel=0`;
    });

    const coverRef = ref<HTMLElement>();
    const videoUnveiled = ref(false);
    const videoPlayer = ref<InstanceType<typeof MktVideoPlayer>>();

    function unveilCover() {
        if (coverRef.value) {
            coverRef.value.style.opacity = '0';
            coverRef.value.addEventListener('transitionend', () => {
                videoUnveiled.value = true;
            });
        }
    }

    function startVideo() {
        unveilCover();
        videoPlayer.value?.play();
    }

    useComponentTracking(section);
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .slice-video--with-shapes {
        padding-bottom: 130px;
        position: relative;
        overflow: hidden;

        &--grey {
            background-color: var(--joy-color-neutral-20);

            .slice-video--with-shapes__title,
            .slice-video--with-shapes__description {
                &,
                * {
                    color: var(--joy-color-neutral-60);
                }

                strong {
                    color: var(--joy-color-neutral-0);
                }
            }
        }

        @include mq.screen_md(min) {
            padding-bottom: initial;
        }

        &__content {
            position: static;
            background: linear-gradient(to bottom, var(--joy-color-neutral-0) 25%, var(--joy-color-primary-50) 10%);

            @include mq.screen_md(min) {
                display: grid;
                background: none;
                grid-template-columns: repeat(2, 1fr);

                > :first-child {
                    grid-row: 2;
                    grid-column: span 2;
                }

                > :nth-child(2) {
                    grid-row: 1;
                    grid-column: 1 / -1;

                    &::after {
                        content: '';
                        position: absolute;
                        top: 0;
                        left: 0;
                        height: 50%;
                        width: 100%;
                        background: var(--joy-color-primary-50);
                        border-radius: 0 999px 999px 0;
                        z-index: -1;
                    }
                }
            }

            @include mq.screen_lg(min) {
                > :first-child {
                    grid-column: 2 / -2;
                }

                > :nth-child(2) {
                    &::after {
                        width: 70%;
                    }
                }
            }

            @include mq.screen_xlg(min) {
                > :nth-child(2) {
                    &::after {
                        width: 70%;
                    }
                }
            }
        }

        &__wrapper {
            position: relative;

            @include mq.screen_lg(min) {
                width: 880px;
            }

            .animate & > * {
                transition-duration: var(--joy-transition-duration-default);
            }
        }

        &__cover {
            transition: opacity var(--joy-transition-duration-long) var(--joy-transition-timing-function);
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            cursor: pointer;
            border-radius: var(--joy-core-radius-4);
            overflow: hidden;

            &-image,
            &-overlay {
                position: absolute;
                width: 100%;
                height: 108%;
                object-fit: cover;
            }

            &-overlay {
                opacity: 0.3;
                background: var(--joy-color-neutral-40);
            }

            &-icon {
                width: 48px;
                height: auto;
                margin-bottom: var(--joy-core-spacing-2);

                @include mq.screen_xs(min) {
                    width: 40px;
                }
                @include mq.screen_sm(min) {
                    width: 65px;
                    margin-bottom: var(--joy-core-spacing-4);
                }
                @include mq.screen_md(min) {
                    width: 100px;
                    margin-bottom: var(--joy-core-spacing-6);
                }
            }

            &-icon-wrapper {
                z-index: 1;
            }
        }

        &__text-wrapper {
            text-align: left;
            padding-top: var(--joy-core-spacing-6);

            @include mq.screen_md(min) {
                padding-bottom: var(--joy-core-spacing-8);
            }

            @include mq.screen_lg(min) {
                width: 60%;
                padding-top: 80px;
            }

            @include mq.screen_xlg(min) {
                width: 58%;
            }
        }

        &__title {
            &,
            * {
                font-size: 32px;
                font-family: var(--joy-font-family-title);
                font-weight: var(--joy-font-weight-normal);
                color: var(--joy-color-neutral-0);

                @include mq.screen_sm(min) {
                    font-size: var(--joy-font-size-secondary-400);
                }
            }
        }

        &__description {
            &,
            * {
                font-size: var(--joy-font-size-primary-400);
                color: var(--joy-color-neutral-0);

                @include mq.screen_md(min) {
                    font-size: var(--joy-font-size-primary-800);
                }
            }
        }

        &__shapes {
            position: absolute;
            bottom: -25px;
            left: 0;
            width: 500px;
            height: auto;
            aspect-ratio: 1 / 1;
            background: url('../../../../assets/svg/video-with-shapes.svg') no-repeat bottom;
            background-size: contain;
            z-index: -2;

            @include mq.screen_md(min) {
                background-position: center;
                top: 60px;
                bottom: 0;
                width: 750px;
            }
        }
    }
</style>
